import React, { useState, useEffect, useRef } from 'react'

const AutoSuggest = ({ suggestions = [], fetchSuggestions, onSelect, placeholder = 'Search…', initialValue, debounceTime = 300 }) => {
  const [displayValue, setDisplayValue] = useState(initialValue?.label || '')
  const [searchTerm, setSearchTerm] = useState('')
  const [filtered, setFiltered] = useState([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef(null)

  // Sync initialValue
  useEffect(() => {
    if (initialValue) {
      setDisplayValue(initialValue.label)
      setSearchTerm('')
      setFiltered([])
      setIsOpen(false)
    }
  }, [initialValue])

  // Close on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
        setActiveIndex(-1)
      }
    }

    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  useEffect(() => {
    let cleanup

    const term = searchTerm.trim()

    if (!term) {
      setFiltered([])
      setIsOpen(false)
      setLoading(false)
      setActiveIndex(-1)
    } else {
      setActiveIndex(-1)

      if (fetchSuggestions) {
        let active = true
        setLoading(true)

        const id = setTimeout(() => {
          fetchSuggestions(term)
            .then((results) => {
              if (!active) return
              setFiltered(results)
              setIsOpen(results.length > 0)
              setLoading(false)
            })
            .catch(() => {
              if (!active) return
              setLoading(false)
            })
        }, debounceTime)

        cleanup = () => {
          active = false
          clearTimeout(id)
        }
      } else {
        const lower = term.toLowerCase()
        const matches = suggestions.filter((s) => s.label.toLowerCase().includes(lower))

        setFiltered(matches)
        setIsOpen(matches.length > 0)
        setLoading(false)
      }
    }

    return cleanup // ✅ always returns something
  }, [searchTerm, suggestions, fetchSuggestions, debounceTime])

  // Handlers
  const handleChange = (e) => {
    const v = e.target.value
    setDisplayValue(v)
    setSearchTerm(v)
  }

  const handleSelect = (item) => {
    setDisplayValue(item.label)
    setSearchTerm('')
    setFiltered([])
    setIsOpen(false)
    onSelect(item)
  }

  const onKeyDown = (e) => {
    if (!isOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0) handleSelect(filtered[activeIndex])
    }
  }

  return (
    <div className="relative w-full max-w-md" ref={containerRef}>
      <input
        type="text"
        className="auto_suggest_input w-full rounded-md border border-[#858585] dark:border-zinc-500 bg-white dark:bg-zinc-700 px-4 py-2 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-zinc-200 focus:ring-blue-500 focus:outline-none"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => filtered.length > 0 && setIsOpen(true)}
        style={{
          width: '100%',
          backgroundColor: '#2d2f44 !important',
          color: 'white',
          borderWidth: '0px !important',
        }}
      />

      {isOpen && (
        <ul
          className="auto_suggest_container absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
          style={{
            backgroundColor: '#2d2f44 !important',
            listStyle: 'none!important',
            textAlign: 'left!important',
            padding: '0px',
            borderWidth: '0px !important',
          }}
        >
          {loading ? (
            <li className="px-3 py-2 text-gray-500">Loading…</li>
          ) : (
            filtered.map((item, idx) => (
              <li
                key={item.value}
                className={`px-3 py-2 cursor-pointer ${idx === activeIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(-1)}
                onMouseDown={() => handleSelect(item)}
              >
                {item.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

export default AutoSuggest
